import dictionary from "../content/dictionary.json";
import type { ApiTerm } from "../content/api";

export type Depth = "plain" | "clear" | "deep";

export type Term = ApiTerm;

let byTerm = new Map<string, Term>(
  dictionary.terms.map((t) => [t.term.toUpperCase(), t as Term]),
);

export function loadTermIndex(terms: Term[]) {
  byTerm = new Map(terms.map((t) => [t.term.toUpperCase(), t]));
}

export function getTerm(term: string): Term | undefined {
  return byTerm.get(term.toUpperCase());
}

export function pickFeaturedTerms(count = 6): Term[] {
  const preferred = [
    "CANNABIS",
    "THC",
    "CBD",
    "TERPENE",
    "TRICHOME",
    "ENDOCANNABINOID",
    "INDICA",
    "SATIVA",
    "EDIBLE",
    "HEMP",
  ];
  const found: Term[] = [];
  for (const key of preferred) {
    const t = byTerm.get(key);
    if (t) found.push(t);
    if (found.length >= count) return found;
  }
  for (const t of byTerm.values()) {
    if (!found.includes(t)) found.push(t);
    if (found.length >= count) break;
  }
  return found;
}

export class KnowledgePanel {
  readonly root: HTMLElement;
  private termEl: HTMLElement;
  private bodyEl: HTMLElement;
  private sourceEl: HTMLAnchorElement;
  private depth: Depth = "plain";
  private current: Term | null = null;
  private buttons: Record<Depth, HTMLButtonElement>;
  private card: HTMLElement;

  constructor(host: HTMLElement = document.body) {
    this.root = document.createElement("aside");
    this.root.className = "knowledge";
    this.root.innerHTML = `
      <div class="knowledge-card" data-card hidden>
        <p class="knowledge-term" data-term></p>
        <p class="knowledge-body" data-body></p>
        <div class="knowledge-actions">
          <button type="button" data-depth="plain">Plain</button>
          <button type="button" data-depth="clear">Clear</button>
          <button type="button" data-depth="deep">Deep</button>
          <a data-source target="_blank" rel="noopener noreferrer">Source</a>
          <button type="button" data-close>Close</button>
        </div>
      </div>
    `;
    host.appendChild(this.root);

    this.card = this.root.querySelector("[data-card]") as HTMLElement;
    this.termEl = this.root.querySelector("[data-term]") as HTMLElement;
    this.bodyEl = this.root.querySelector("[data-body]") as HTMLElement;
    this.sourceEl = this.root.querySelector("[data-source]") as HTMLAnchorElement;

    this.buttons = {
      plain: this.root.querySelector('[data-depth="plain"]') as HTMLButtonElement,
      clear: this.root.querySelector('[data-depth="clear"]') as HTMLButtonElement,
      deep: this.root.querySelector('[data-depth="deep"]') as HTMLButtonElement,
    };

    Object.entries(this.buttons).forEach(([depth, btn]) => {
      btn.addEventListener("click", () => this.setDepth(depth as Depth));
    });

    this.root.querySelector("[data-close]")?.addEventListener("click", () => this.close());

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.card.classList.contains("is-open")) this.close();
    });
  }

  open(termKey: string) {
    const term = getTerm(termKey);
    if (!term) return;
    this.current = term;
    this.depth = "plain";
    this.termEl.textContent = term.term;
    this.renderBody();
    this.card.hidden = false;
    this.card.classList.add("is-open");
    if (term.source) {
      this.sourceEl.href = term.source;
      this.sourceEl.textContent = term.sourceLabel
        ? `Source: ${term.sourceLabel}`
        : "Source";
      this.sourceEl.style.display = "";
    } else {
      this.sourceEl.style.display = "none";
    }
  }

  close() {
    this.card.classList.remove("is-open");
    this.card.hidden = true;
    this.current = null;
  }

  private setDepth(depth: Depth) {
    this.depth = depth;
    this.renderBody();
  }

  private renderBody() {
    if (!this.current) return;
    const text =
      this.depth === "plain"
        ? this.current.plain
        : this.depth === "clear"
          ? this.current.clear
          : this.current.deep;
    this.bodyEl.textContent = text;
    (Object.keys(this.buttons) as Depth[]).forEach((d) => {
      this.buttons[d].classList.toggle("is-active", d === this.depth);
    });
  }
}
